import { useMemo } from 'react';

import { useIsOnLocaleSubfolderRoute } from '@coursera-web/internationalization-lib';

import getFacets from 'bundles/search-common/components/filters/getFacets';
import useFacetOrder from 'bundles/search-common/components/filters/useFacetOrder';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE } from 'bundles/search-common/constants/filter';
import { useShowCourseraTier } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { type FilterSortConfig, sortFilterItems } from 'bundles/search-common/utils/filterSortingUtils';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import useStableSearchResults from 'bundles/search-common/utils/useStableSearchResults';
import { createFilterKeyToOptionsMap } from 'bundles/search-common/utils/utils';

type UseSearchFiltersOptions = {
  /** Custom order for facets. If not provided, uses DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE */
  facetOrder?: string[];
  /** Search index ID to fetch data from. Defaults to SEARCH_MAIN_INDEX_ID */
  searchIndexId?: string;
  /** Use stable search results that don't flicker during loading. Defaults to false */
  useStableResults?: boolean;
  /** Configuration for sorting filter items. If provided, applies sorting to all filter options */
  sortConfig?: FilterSortConfig;
};

/**
 * Unified hook that provides everything needed for search filters in one call.
 *
 * This hook consolidates all filter-related logic including configurations,
 * search data, and processed filter options. It eliminates the need to manually
 * combine useSearchFilterConfigs() + createFilterKeyToOptionsMap() + search data.
 *
 * @param options - Configuration options for customizing filter behavior
 * @returns Complete filter data including configs, options map, and search results
 *
 * @example
 * // Basic usage - gets everything with defaults
 * const { filterGroupConfigs, filterKeyToOptionsMap, searchData } = useSearchFilters();
 *
 * @example
 * // Custom facet order
 * const filters = useSearchFilters({
 *   facetOrder: ['topic', 'language', 'partners']
 * });
 *
 * @example
 * // Different search index
 * const filters = useSearchFilters({
 *   searchIndexId: 'custom-search-index'
 * });
 *
 * @example
 * // Stable results that don't flicker during loading
 * const filters = useSearchFilters({
 *   useStableResults: true
 * });
 */
export const useSearchFilters = (options?: UseSearchFiltersOptions) => {
  const {
    facetOrder: customFacetOrder,
    searchIndexId = SEARCH_MAIN_INDEX_ID,
    useStableResults = false,
    sortConfig,
  } = options || {};

  // Context-based configurations
  const shouldShowCourseraPlusFilter = useShowCourseraTier();
  const isSEOEntityPage = useIsEqp();
  const shouldHideSkillFilters = useIsOnLocaleSubfolderRoute() && isSEOEntityPage;

  // Compute facet order
  const facetOrder = useFacetOrder(customFacetOrder ?? DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE);

  // Get filter configurations
  const filterGroupConfigs = useMemo(
    () =>
      getFacets({
        shouldIncludeCourseraPlusFacet: shouldShowCourseraPlusFilter,
        facetOrder,
        shouldExcludeSkillsFacet: shouldHideSkillFilters,
      }),
    [shouldShowCourseraPlusFilter, shouldHideSkillFilters, facetOrder]
  );

  // Get search data (stable or regular)
  const { getSearchResults } = useSearch();
  const regularSearchData = getSearchResults(searchIndexId);
  const stableSearchData = useStableSearchResults(searchIndexId);

  // During SSR, useStableSearchResults always returns undefined because it
  // relies on useEffect to persist data. Fall back to regularSearchData so
  // that filter taxonomy is available in server-rendered HTML.
  const searchData = useStableResults ? (stableSearchData ?? regularSearchData) : regularSearchData;
  const { facets, facetFilters } = searchData || {};
  const activeFacetFilters = useMemo(() => facetFilters || [], [facetFilters]);

  // Create base filter options map
  const baseFilterKeyToOptionsMap = useMemo(() => {
    return createFilterKeyToOptionsMap(facets, activeFacetFilters, facetOrder, facets);
  }, [facets, activeFacetFilters, facetOrder]);

  // Apply sorting to the base map if sortConfig is provided
  const filterKeyToOptionsMap = useMemo(() => {
    if (!sortConfig) {
      return baseFilterKeyToOptionsMap;
    }

    const sortedMap: typeof baseFilterKeyToOptionsMap = {};
    Object.entries(baseFilterKeyToOptionsMap).forEach(([key, items]) => {
      if (items) {
        sortedMap[key] = sortFilterItems(items, key, sortConfig);
      }
    });
    return sortedMap;
  }, [baseFilterKeyToOptionsMap, sortConfig]);

  return {
    // Primary data most components need
    filterGroupConfigs,
    facetOrder,
    filterKeyToOptionsMap,
    searchData,

    // Individual pieces for specific use cases
    facets,
    facetFilters: activeFacetFilters,

    // Computed flags for conditional logic
    shouldShowCourseraPlusFilter,
    shouldHideSkillFilters,
  };
};
